import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { StaffCustomField } from "@/lib/api/staff-custom-fields";
import { cn } from "@/lib/utils";

interface CustomFieldRendererProps {
  field: StaffCustomField;
  value: any;
  onChange: (value: any) => void;
  error?: string;
  disabled?: boolean;
}

export const CustomFieldRenderer = ({
  field,
  value,
  onChange,
  error,
  disabled = false,
}: CustomFieldRendererProps) => {
  const renderField = () => {
    switch (field.type) {
      case "text":
      case "email":
        return (
          <Input
            id={field.name}
            type={field.type}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            className={cn(error && "border-red-500 focus:border-red-500")}
          />
        );

      case "number":
        return (
          <Input
            id={field.name}
            type="number"
            value={value || ""}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
            placeholder={field.placeholder}
            disabled={disabled}
            min={field.validation?.min}
            max={field.validation?.max}
            className={cn(error && "border-red-500 focus:border-red-500")}
          />
        );

      case "textarea":
        return (
          <Textarea
            id={field.name}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            minLength={field.validation?.minLength}
            maxLength={field.validation?.maxLength}
            className={cn(error && "border-red-500 focus:border-red-500")}
          />
        );

      case "date":
        return (
          <Input
            id={field.name}
            type="date"
            value={value || ""}
            onChange={(e) => onChange(e.target.value || undefined)}
            disabled={disabled}
            className={cn(error && "border-red-500 focus:border-red-500")}
          />
        );

      case "select":
        return (
          <Select
            value={value || ""}
            onValueChange={(val) => onChange(val || undefined)}
            disabled={disabled}
          >
            <SelectTrigger className={cn(error && "border-red-500 focus:border-red-500")}>
              <SelectValue placeholder={field.placeholder || "Select..."} />
            </SelectTrigger>
            <SelectContent>
              {[...(field.options || [])]
                .sort((a, b) =>
                  (a.label || "").localeCompare(b.label || "", undefined, { sensitivity: "base" })
                )
                .map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        );

      case "radio":
        return (
          <RadioGroup
            value={value || ""}
            onValueChange={(val) => onChange(val || undefined)}
            disabled={disabled}
          >
            {[...(field.options || [])]
              .sort((a, b) =>
                (a.label || "").localeCompare(b.label || "", undefined, { sensitivity: "base" })
              )
              .map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={option.value} id={`${field.name}-${option.value}`} />
                  <Label htmlFor={`${field.name}-${option.value}`} className="cursor-pointer">
                    {option.label}
                  </Label>
                </div>
              ))}
          </RadioGroup>
        );

      case "checkbox":
        return (
          <div className="flex items-center space-x-2">
            <Checkbox
              id={field.name}
              checked={value === true}
              onCheckedChange={(checked) => onChange(checked === true)}
              disabled={disabled}
            />
            <Label htmlFor={field.name} className="cursor-pointer">
              {field.placeholder || field.label}
            </Label>
          </div>
        );

      case "file":
        return (
          <Input
            id={field.name}
            type="file"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                // For file uploads, you might want to upload to storage first
                // For now, we'll just store the file name
                onChange(file.name);
              }
            }}
            disabled={disabled}
            className={cn(error && "border-red-500 focus:border-red-500")}
          />
        );

      default:
        return (
          <Input
            id={field.name}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            className={cn(error && "border-red-500 focus:border-red-500")}
          />
        );
    }
  };

  return (
    <div>
      <Label htmlFor={field.name} className="flex items-center gap-1 mb-2">
        {field.label}
        {field.required && <span className="text-red-500">*</span>}
      </Label>
      {renderField()}
      {error && (
        <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
          <span>⚠</span>
          {error}
        </p>
      )}
    </div>
  );
};
